/**
 * Live drift watcher — bounded control plane for drift detection.
 *
 * Topology: single Node process, no subprocesses, no AI calls, no network.
 *   Trigger  : chokidar primary; mtime polling fallback (kicks in if no
 *              chokidar event arrives within FALLBACK_AFTER_MS — covers the
 *              macOS cloud-sync edge case documented in learn/watch.ts).
 *              Polling is diff-based: only runs a drift check when files
 *              actually changed since the last poll. No change → no tick.
 *   Verifier : runDriftCheck({ incremental: true }) — deterministic, ms-fast.
 *   Stop     : SIGINT/SIGTERM handler, --max-iterations, --idle-stop.
 *   Budget   : zero tokens. Drift detection is the non-negotiable
 *              "no AI in core logic" path.
 *   Logging  : every tick appends to .cai/events.jsonl via logEvent so
 *              timeline / watchdog/brief can read it.
 *
 * Rationale: this is a *read-only* loop. It never mutates files — that's
 * what `cai fix` is for, and the post-commit hook (`watch.ts`) already
 * gates that with a score threshold. The live watcher only surfaces
 * drift deltas to the developer.
 */
import { mkdirSync, existsSync, writeFileSync, unlinkSync, readFileSync } from "node:fs";
import { join } from "node:path";
import chalk from "chalk";
import { runDriftCheck } from "./drift/index.js";
import { logEvent } from "./events.js";
import { recentlyChangedFiles } from "./watch-poller.js";
import { WATCH_IGNORE_GLOBS } from "./watch-ignore.js";
import type { CaiConfig } from "./types.js";

/** Time without any file event before the polling fallback activates. */
const FALLBACK_AFTER_MS = 5_000;
/** Default polling interval when fallback is active. */
const POLL_MS = 5_000;
/** Default debounce window per file path. */
const DEBOUNCE_MS = 300;
/** Timeout for chokidar's close() during shutdown — don't block on a stuck watcher. */
const CLOSE_TIMEOUT_MS = 2_000;
/** Upper bound on the entire shutdown wait before forceStop fires. */
const SHUTDOWN_TIMEOUT_MS = 30_000;
/**
 * Headroom added on top of `idleStopMs * 2` when deriving the safety net from
 * an idle-stop config. The idle-stop check fires at `pollMs` granularity
 * (default 5s), so the safety net must sit far enough behind the idle window
 * to guarantee the idle check fires first. The 2x absorbs `pollMs`-granularity
 * slack; the +IDLE_SAFETY_HEADROOM_MS is belt-and-suspenders.
 */
const IDLE_SAFETY_HEADROOM_MS = 10_000;
/** Filename for the running-watcher PID file inside .cache/. */
const PID_FILENAME = "watch-live.pid";

/** Optional chokidar — loaded lazily because it is an optionalDependency. */
type ChokidarWatcher = {
  on(event: string, listener: (path: string) => void): ChokidarWatcher;
  close(): Promise<void>;
};
type ChokidarFactory = (paths: string[], opts: Record<string, unknown>) => ChokidarWatcher;

export interface LiveWatchOptions {
  /** Override debounce window per file (default 300ms). */
  debounceMs?: number;
  /** Stop after this many drift ticks (default 0 = unlimited). */
  maxIterations?: number;
  /** Stop after this many minutes with no file events (default 0 = disabled). */
  idleStopMinutes?: number;
  /** Glob patterns to watch relative to projectRoot (default: src/lib/app/packages). */
  paths?: string[];
  /** Force-start even if a stale PID file is found. */
  force?: boolean;
  /** When set, watch loops this many times then exits (used by tests). */
  testMaxIterations?: number;
  /** Test hook: called after each drift tick. */
  onTick?: (info: { score: number; errors: number; warnings: number; triggeredBy: string }) => void;
  /** Test hook: called when stop is requested. */
  onStop?: (reason: StopReason) => void;
  /** Disable chokidar and start in polling-only mode. */
  noChokidar?: boolean;
  /** Override the polling interval (default 5000ms). */
  pollMs?: number;
  /** Override the FALLBACK_AFTER_MS threshold (default 5000ms). */
  fallbackAfterMs?: number;
  /** Override the chokidar close timeout (default 2000ms). Lower for hermetic tests. */
  closeTimeoutMs?: number;
  /** Upper bound on the entire shutdown wait, in ms (default 30000).
   *  Forces forceStop if no other stop reason fires — prevents test deadlock
   *  if testMaxIterations / idleStopMinutes / SIGINT are all unset. */
  shutdownTimeoutMs?: number;
}

/** Result of one drift check, normalized for the watcher. */
export interface TickResult {
  score: number;
  errors: number;
  warnings: number;
  report: Awaited<ReturnType<typeof runDriftCheck>>;
}

/** Pure: runs the drift check and returns counts. No logging, no opts. */
export async function executeTick(config: CaiConfig): Promise<TickResult> {
  const report = await runDriftCheck(config, { incremental: true });
  const errors = report.issues.filter((i) => i.severity === "error").length;
  const warnings = report.issues.filter((i) => i.severity === "warning").length;
  return { score: report.score, errors, warnings, report };
}

/** Format Date as HH:MM:SS for console output. */
export function formatHms(d: Date = new Date()): string {
  return d.toISOString().slice(11, 19);
}

/** Render a tick result: log to events.jsonl, print to console on drift, fire onTick. */
export function reportTick(
  config: CaiConfig,
  triggeredBy: string,
  result: TickResult,
  onTick?: LiveWatchOptions["onTick"]
): void {
  const { score, errors, warnings } = result;
  const summary = `${score}/100 (${errors}e/${warnings}w) [trigger: ${triggeredBy}]`;
  logEvent(config, "note", `watch tick: ${summary}`);
  if (score < 100) {
    const ts = formatHms();
    console.log(`  ${chalk.cyan(ts)}  ${chalk.yellow("⚠ drift")}  ${chalk.dim(summary)}`);
  }
  if (onTick) onTick({ score, errors, warnings, triggeredBy });
}

/**
 * True when the current iteration count has reached the configured ceiling.
 *
 * The `mode` distinguishes "pre-check" (before running the tick) from
 * "post-check" (after running the tick). Pre-check uses strict greater-than
 * so a budget of N allows N ticks to run; post-check uses greater-or-equal
 * so the Nth tick trips the stop. Without this distinction the budget is
 * off-by-one — `testMaxIterations: 1` would silently run zero ticks because
 * `1 >= 1` is true before the first tick executes.
 */
export function isBudgetExhausted(
  iterations: number,
  maxIterations: number,
  testMaxIterations: number | undefined,
  mode: "pre" | "post"
): boolean {
  const cmp = mode === "pre" ? (a: number, b: number) => a > b : (a: number, b: number) => a >= b;
  if (maxIterations > 0 && cmp(iterations, maxIterations)) return true;
  if (testMaxIterations !== undefined && cmp(iterations, testMaxIterations)) return true;
  return false;
}

/**
 * Pure: compute the shutdown safety-net timeout.
 *
 * Precedence:
 *   1. If the caller explicitly passes `shutdownTimeoutMs`, honor it as-is.
 *      Callers opting into a value get that value — no silent bumping.
 *   2. Else if `idleStopMs > 0`, derive from `idleStopMs * 2 + IDLE_SAFETY_HEADROOM_MS`
 *      so the safety net sits behind the idle-stop check (which fires at
 *      `pollMs` granularity).
 *   3. Else, use the default `SHUTDOWN_TIMEOUT_MS`.
 */
export function computeSafetyNetTimeoutMs(
  opts: LiveWatchOptions,
  idleStopMs: number
): number {
  if (opts.shutdownTimeoutMs !== undefined) return opts.shutdownTimeoutMs;
  if (idleStopMs > 0) return idleStopMs * 2 + IDLE_SAFETY_HEADROOM_MS;
  return SHUTDOWN_TIMEOUT_MS;
}

export type StopReason = "sigint" | "sigterm" | "max-iterations" | "idle-stop";

/**
 * Run the live drift watcher until stopped.
 *
 * Invariant (mirrors session-auto.ts): if anything goes wrong the watcher
 * exits cleanly — the PID file is released, signal handlers are removed,
 * and chokidar is closed within closeTimeoutMs. A failed invocation never
 * leaves the project in a state where a second watch is refused.
 */
export async function runLiveWatch(
  config: CaiConfig,
  opts: LiveWatchOptions = {}
): Promise<void> {
  const projectRoot = config.projectRoot;
  const debounceMs = opts.debounceMs ?? DEBOUNCE_MS;
  const maxIterations = opts.maxIterations ?? 0;
  const idleStopMs = (opts.idleStopMinutes ?? 0) * 60_000;
  const watchPaths = opts.paths ?? defaultWatchPaths();
  const testMaxIterations = opts.testMaxIterations;
  const pollMs = opts.pollMs ?? POLL_MS;
  const fallbackAfterMs = opts.fallbackAfterMs ?? FALLBACK_AFTER_MS;

  // Refuse to start if another watcher is running unless --force.
  if (!acquirePid(projectRoot, opts.force ?? false)) {
    console.error(chalk.red("Another live watcher is already running for this project."));
    console.error(chalk.dim(`  Inspect: cat ${pidPath(projectRoot)}`));
    console.error(chalk.dim(`  Force:   cai watch --mode=live --force`));
    return;
  }

  console.log(chalk.bold(`\n  cai watch --mode=live`));
  console.log(chalk.dim(`  project: ${projectRoot}`));
  console.log(chalk.dim(`  paths:   ${watchPaths.join(", ")}`));
  console.log(chalk.dim(`  debounce: ${debounceMs}ms · max-iterations: ${maxIterations || "∞"} · idle-stop: ${opts.idleStopMinutes || "off"} min`));
  console.log(chalk.dim(`  Press Ctrl-C to stop.\n`));

  // One debounce map: file path → timer. Replaces the previous two-tier
  // (debounced → scheduleTick → pending) which doubled the effective delay.
  const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // lastFileEventAt tracks real file events for idle-stop detection.
  // Ticks themselves don't reset it — a tick with score=100 should not
  // keep the watcher alive indefinitely.
  let lastFileEventAt = Date.now();
  let iterations = 0;
  let stopReason: StopReason | null = null;
  let fallbackAnnounced = false;

  // Single-promise stop barrier. Resolves the instant requestStop fires —
  // no 100ms polling, no race window. The shutdownTimeoutMs race below
  // is the safety net for cases where no stop path is configured.
  let resolveStop: (() => void) | null = null;
  const stopped = new Promise<void>((resolve) => {
    resolveStop = resolve;
  });

  // Wire signal handlers — graceful shutdown.
  const sigintHandler = () => requestStop("sigint");
  const sigtermHandler = () => requestStop("sigterm");
  process.on("SIGINT", sigintHandler);
  process.on("SIGTERM", sigtermHandler);

  function requestStop(reason: StopReason): void {
    if (stopReason) return;
    stopReason = reason;
    clearInterval(pollInterval);
    if (resolveStop) resolveStop();
    if (opts.onStop) opts.onStop(reason);
  }

  async function runTick(
    triggeredBy: string,
    tickOpts: { countTowardBudget?: boolean } = {}
  ): Promise<void> {
    if (stopReason) return;
    const countTowardBudget = tickOpts.countTowardBudget ?? true;
    if (countTowardBudget) {
      iterations++;
      // Pre-check: if iterations is already past the ceiling, skip the check.
      if (isBudgetExhausted(iterations, maxIterations, testMaxIterations, "pre")) {
        requestStop("max-iterations");
        return;
      }
    }
    try {
      const result = await executeTick(config);
      // Re-check after the await — a stop may have fired while we were running.
      if (stopReason) return;
      reportTick(config, triggeredBy, result, opts.onTick);
      // Post-check: this run consumed its budget slot.
      if (countTowardBudget && isBudgetExhausted(iterations, maxIterations, testMaxIterations, "post")) {
        requestStop("max-iterations");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      logEvent(config, "note", `watch tick error: ${msg}`);
      console.error(chalk.red(`  watch tick failed: ${msg}`));
    }
  }

  function scheduleTick(absPath: string): void {
    const existing = debounceTimers.get(absPath);
    if (existing) clearTimeout(existing);
    const handle = setTimeout(() => {
      debounceTimers.delete(absPath);
      void runTick(absPath);
    }, debounceMs);
    debounceTimers.set(absPath, handle);
  }

  function onFileEvent(path: string): void {
    lastFileEventAt = Date.now();
    scheduleTick(path);
  }

  // Try to load chokidar; fall back to polling-only if unavailable.
  let watcher: ChokidarWatcher | null = null;
  if (!opts.noChokidar) {
    try {
      const mod = (await import("chokidar")) as unknown as { default?: ChokidarFactory } & ChokidarFactory;
      const factory: ChokidarFactory = (mod.default ?? mod) as ChokidarFactory;
      watcher = factory(
        watchPaths.map((p) => join(projectRoot, p)),
        {
          ignored: [...WATCH_IGNORE_GLOBS],
          ignoreInitial: true,
          persistent: true,
        }
      );
      watcher.on("add", onFileEvent);
      watcher.on("change", onFileEvent);
      watcher.on("unlink", onFileEvent);
      console.log(chalk.dim(`  trigger: chokidar (event-driven)`));
    } catch {
      console.log(chalk.dim(`  trigger: polling only (chokidar not available)`));
    }
  } else {
    console.log(chalk.dim(`  trigger: polling only (CAI_NO_CHOKIDAR=1)`));
  }

  // Polling fallback — diff-based. Tracks the last poll's mtime-snapshot
  // and only triggers a tick when something actually changed since then.
  // This is the big win over the previous version: previously polling
  // fired runTick unconditionally every pollMs (CPU burn). Now it only
  // fires when recentlyChangedFiles returns non-empty.
  let lastPollAt = Date.now();
  const pollInterval = setInterval(() => {
    if (stopReason) return;
    const now = Date.now();
    const silentFor = now - lastFileEventAt;
    const shouldPoll = watcher === null || silentFor > fallbackAfterMs;
    if (shouldPoll) {
      if (!fallbackAnnounced) {
        fallbackAnnounced = true;
        if (watcher === null) {
          console.log(chalk.dim(`  trigger: polling (no chokidar)`));
        } else {
          console.log(chalk.dim(`  fallback: polling engaged after ${fallbackAfterMs / 1000}s of silence`));
        }
      }
      const sinceMs = now - lastPollAt;
      lastPollAt = now;
      const changed = recentlyChangedFiles(projectRoot, sinceMs);
      if (changed.length > 0) {
        void runTick("poll-fallback");
      }
    }
    // Idle-stop is driven by real file events, not by ticks. A tick that
    // returns score=100 keeps the watcher alive only as long as the user
    // is still editing.
    if (idleStopMs > 0 && now - lastFileEventAt > idleStopMs) {
      requestStop("idle-stop");
    }
  }, pollMs);

  // Initial poll — surface drift immediately when watcher starts.
  // countTowardBudget: false — users expect their first reaction to happen
  // for free. Subsequent ticks count toward max-iterations as normal.
  await runTick("initial", { countTowardBudget: false });

  try {
    // Single-promise stop barrier — resolves the instant requestStop fires,
    // or when the shutdownTimeoutMs safety net trips. No 100ms polling.
    // When --idle-stop is set, the safety net must be strictly behind the
    // idle window or it races and wins with "max-iterations" instead.
    const timeoutMs = computeSafetyNetTimeoutMs(opts, idleStopMs);
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<void>((resolveTimeout) => {
      timeoutHandle = setTimeout(() => {
        // Force-stop the watcher when no stop path fires. Marks stopReason
        // so the rest of teardown behaves normally. Prevents test deadlock
        // if testMaxIterations/idleStopMinutes/SIGINT are all unset.
        requestStop("max-iterations");
        resolveTimeout();
      }, timeoutMs);
    });
    await Promise.race([stopped, timeoutPromise]);
    if (timeoutHandle) clearTimeout(timeoutHandle);
  } finally {
    // Teardown must run on any exit path — including uncaught throws
    // between the initial tick and stopReason being set. Mirrors the
    // session-auto invariant: a failed invocation never leaves handlers
    // installed, the PID file held, or chokidar unclosed.
    //
    // Order matters: close chokidar BEFORE clearing debounce timers so no
    // new scheduleTick calls can land after timers are cleared.
    if (watcher) {
      try {
        await withTimeout(watcher.close(), opts.closeTimeoutMs ?? CLOSE_TIMEOUT_MS);
      } catch {
        // best-effort shutdown
      }
    }
    for (const handle of debounceTimers.values()) clearTimeout(handle);
    debounceTimers.clear();
    try {
      process.off("SIGINT", sigintHandler);
      process.off("SIGTERM", sigtermHandler);
    } catch {
      // process may be in a degraded state — best-effort
    }
    releasePid(projectRoot);
  }

  console.log(chalk.dim(`\n  watch-live: stopped (${stopReason})`));
}

function defaultWatchPaths(): string[] {
  return ["src", "lib", "app", "packages", "*.config.*", "package.json", "tsconfig.json", ".env"];
}

function pidPath(projectRoot: string): string {
  return join(projectRoot, ".cai", ".cache", PID_FILENAME);
}

function acquirePid(projectRoot: string, force: boolean): boolean {
  const path = pidPath(projectRoot);
  if (existsSync(path)) {
    const existing = readFileSync(path, "utf8").trim();
    const pid = Number(existing);
    if (!force && Number.isFinite(pid) && pid > 0 && processExists(pid)) {
      return false;
    }
    // Stale PID file — overwrite below.
  }
  const dir = join(projectRoot, ".cai", ".cache");
  if (!existsSync(dir)) {
    try {
      mkdirSync(dir, { recursive: true });
    } catch {
      // PID file write below will fail; treat as acquired so the watcher can still run.
      return true;
    }
  }
  try {
    writeFileSync(path, String(process.pid), "utf8");
  } catch {
    // PID tracking is best-effort.
  }
  return true;
}

function releasePid(projectRoot: string): void {
  const path = pidPath(projectRoot);
  if (!existsSync(path)) return;
  const existing = readFileSync(path, "utf8").trim();
  if (existing === String(process.pid)) {
    try {
      unlinkSync(path);
    } catch {
      // best-effort
    }
  }
}

function processExists(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | void> {
  return Promise.race([
    promise,
    new Promise<void>((resolveTimeout) => setTimeout(resolveTimeout, ms)),
  ]);
}